import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { StructureSelection } from 'src/meetings/detail/tabs/meeting-note/StructureSelection';
import type { AgendaOption } from 'src/meetings/hooks/useAgendaOptions';
import { ExampleStack, type Example } from '../';

const TEMPLATE_NAMES = [
  'Annual review',
  'Quarterly review',
  'Initial discovery meeting',
  'Cashflow planning session',
  'Retirement options review',
  'Investment strategy review',
  'Protection and insurance review',
  'Estate planning and IHT discussion',
  'Mortgage and lending review',
  'Business owner planning meeting',
  'Pension consolidation review',
  'ISA and general investment account review',
  'Vulnerable client check-in',
  'Family wealth planning meeting',
  'Ongoing service annual suitability assessment for accumulating clients',
];

function template(displayName: string, index: number): TemplateWithoutSections {
  return {
    id: `template-${index}`,
    displayName,
    description: '',
    supportsMulticlient: false,
    eligibleForSummarisation: true,
    templateType: 'MEETING_NOTE',
    isActive: true,
    isDefault: index === 0,
    markdownImageCollectionId: null,
  };
}

function agendaOption(label: string, index: number): AgendaOption {
  return {
    kind: index % 2 === 0 ? 'generated' : 'uploaded',
    id: `agenda-${index}`,
    label,
    createdAt: '2026-09-30T09:00:00Z',
    isDocx: index % 2 === 0,
  };
}

const templates = TEMPLATE_NAMES.map(template);
const agendaOptions = TEMPLATE_NAMES.map((name, index) =>
  agendaOption(`${name} agenda.${index % 2 === 0 ? 'docx' : 'pdf'}`, index),
);

const noop = () => {};

function longLists(): Example {
  return {
    description:
      'Fifteen templates and fifteen agendas: both dropdowns should scroll inside a capped height and open with a focused search box (ENG-1567)',
    render: (
      <StructureSelection
        templates={templates}
        agendaOptions={agendaOptions}
        isGenerating={false}
        onGenerateTemplate={noop}
        onGenerateAgenda={noop}
      />
    ),
  };
}

function shortLists(): Example {
  return {
    description: 'Three of each: below the search threshold, so no search box appears',
    render: (
      <StructureSelection
        templates={templates.slice(0, 3)}
        agendaOptions={agendaOptions.slice(0, 3)}
        isGenerating={false}
        onGenerateTemplate={noop}
        onGenerateAgenda={noop}
      />
    ),
  };
}

function emptyLists(): Example {
  return {
    description: 'Nothing to choose from: both dropdowns show "No options available"',
    render: (
      <StructureSelection
        templates={[]}
        agendaOptions={[]}
        isGenerating={false}
        onGenerateTemplate={noop}
        onGenerateAgenda={noop}
      />
    ),
  };
}

export default function MeetingNoteStructureSandbox() {
  return <ExampleStack examples={[longLists(), shortLists(), emptyLists()]} />;
}
